import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import Selectors from 'src/utils/selectors';
import LiteralsService from 'src/services/literals/LiteralsService';
import FormatService from 'src/services/format.service';
import React from 'react';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import BudgetService from 'src/services/budget.service';
import { useSyncingAccounts } from 'src/hooks/useSyncingAccounts';
import styles from './TrackerHeader.module.scss';
import { BudgetSummaryStatus } from 'src/types/budget-summary-status.enum';

interface TrackerHeaderProps {
    budgetGroupName: string;
    budgetMonthlyAmount: number;
    parentName: string;
    status: BudgetSummaryStatus;
}

export default function TrackerHeader({
    budgetGroupName,
    budgetMonthlyAmount,
    parentName,
    status
}: TrackerHeaderProps): JSX.Element {
    const { isMinWidthMedium } = useBreakpoints();
    const iconComponent: JSX.Element = BudgetService.getBudgetIconComponent(budgetGroupName);
    const { isSyncingAccounts } = useSyncingAccounts();
    const showIcon: boolean = !parentName || isMinWidthMedium;
    const statusClassName: string =
        status === BudgetSummaryStatus.OVER
            ? styles.bad
            : status === BudgetSummaryStatus.AT_RISK
            ? styles.warning
            : styles.good;

    return (
        <div className={styles.header} data-cy={Selectors.SpendSmart.TRACKER_HEADER}>
            {showIcon && (
                <div
                    className={
                        isSyncingAccounts ? styles.iconCircleSkeleton : `${styles.iconCircle} ${statusClassName}`
                    }
                    data-cy={Selectors.SpendSmart.TRACKER_HEADER_ICON}
                >
                    {iconComponent}
                </div>
            )}
            <div>
                {parentName && (
                    <Typography
                        variant={TypographyVariants.LEGAL_SECONDARY}
                        className={styles.parentName}
                        component="h3"
                    >
                        {parentName}
                    </Typography>
                )}
                <Typography
                    variant={TypographyVariants.HEADING_XS}
                    component={parentName ? 'h4' : 'h3'}
                    data-cy={Selectors.SpendSmart.TRACKER_HEADER_TITLE}
                >
                    {budgetGroupName}
                </Typography>
                {isMinWidthMedium ? (
                    <Typography variant={TypographyVariants.BODY}>
                        {LiteralsService.getLiteral('Budget.Current.monthlyAmount')}
                    </Typography>
                ) : (
                    <Typography variant={TypographyVariants.SUBTEXT}>
                        {LiteralsService.getLiteral('Budget.Current.moAmount')}
                    </Typography>
                )}
                <Typography
                    variant={isMinWidthMedium ? TypographyVariants.BODY_BOLD : TypographyVariants.SUBTEXT_BOLD}
                    data-cy={Selectors.SpendSmart.TRACKER_HEADER_AMOUNT}
                >
                    {FormatService.format(budgetMonthlyAmount)}
                </Typography>
            </div>
        </div>
    );
}
